import React, { useState, useCallback, memo } from 'react';
import { showNotification } from '../utils/notification';
import { useBasicInfo } from '../data/menuData';

const ContactSection = () => {
  const { basicInfo, loading } = useBasicInfo();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    date: '',
    guests: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  }, []);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const googleScriptUrl = import.meta.env.VITE_WEB_URL_GOOGLE_SCRIPT;
      
      console.log(googleScriptUrl);
      
      // if (!googleScriptUrl) {
      //   throw new Error('Google Apps Script URL not configured. Please check your .env file.');
      // }

      // Create JSON data to match your Google Apps Script expectations
      const jsonData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        subject: formData.subject,
        date: formData.date,
        guests: formData.guests,
        message: formData.message
      };

      console.log('Sending JSON data:', jsonData);

      // Use fetch to send JSON data
      const response = await fetch(googleScriptUrl, {
        method: 'POST',
        mode: 'no-cors', // This is important for Google Apps Script remove when its deployed
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(jsonData),
      });

      console.log('Response:', response);
      
      // With no-cors mode, we can't read the response, so we assume success
      // if no error was thrown and response type is opaque
      if (response.type === 'opaque') {
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          date: '',
          guests: '',
          message: ''
        });
        showNotification('Message sent successfully! Chef Aparna will get back to you soon.', 'success');
      } else if (response.ok) {
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          date: '',
          guests: '',
          message: ''
        });
        showNotification('Message sent successfully! Chef Aparna will get back to you soon.', 'success');
      } else {
        throw new Error(`Failed to send message. Status: ${response.status}`);
      }
    } catch (error) {
      console.error('Error sending form data:', error);
      console.error('Error details:', {
        message: error.message,
        status: error.status
      });
      
      let errorMessage = 'Failed to send message. Please try again later.';
      if (error.message.includes('timeout')) {
        errorMessage = 'Request timed out. Please check your internet connection and try again.';
      } else if (error.message.includes('Network Error')) {
        errorMessage = 'Network error. Please check your internet connection and try again.';
      } else if (error.message.includes('CORS')) {
        errorMessage = 'CORS error. Please check your Google Apps Script configuration.';
      }
      
      showNotification(errorMessage, 'error');
    } finally {
      setIsSubmitting(false);
    }
  }, [formData]);

  return (
    <section id="contact" className="py-20 bg-gradient-to-br from-[#F8E8D2]/20 to-white">
      <div className="max-w-6xl mx-auto px-5">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-segoe font-bold text-gray-800 mb-4">Contact Us</h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto"> Get in Touch for Custom Cake Orders, Bulk Party Requests, Feedback, or just to say hello! </p>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
          {/* Contact Info */}
          <div className="space-y-4 sm:space-y-6">

            {loading ? (
              // Loading state
              <div className="flex items-center justify-center p-8">
                <div className="text-center">
                  <i className="fas fa-spinner fa-spin text-2xl text-primary mb-2"></i>
                  <p className="text-gray-600">Loading contact information...</p>
                </div>
              </div>
            ) : basicInfo ? (
              <>
                <ContactInfoCard 
                  icon="fas fa-map-marker-alt"
                  title="Restaurant Location"
                  content={basicInfo.address || "Address not available"}
                />
                
                <ContactInfoCard 
                  icon="fas fa-phone"
                  title="Phone Number"
                  content={basicInfo.contactNo || "Phone number not available"}
                />
                
                <ContactInfoCard 
                  icon="fas fa-envelope"
                  title="Email Address"
                  content={basicInfo.email || "Email not available"}
                />
                
                <ContactInfoCard 
                  icon="fas fa-clock"
                  title="Opening Hours"
                  content="Monday - Saturday: 11:00 AM - 10:00 PM<br />Sunday: 12:00 PM - 9:00 PM"
                />
              </>
            ) : (
              // Fallback if no data
              <div className="text-center p-8 text-gray-600">
                <i className="fas fa-exclamation-triangle text-2xl mb-2"></i>
                <p>Contact information not available</p>
              </div>
            )}
          </div>
          
          {/* Contact Form */}
          <div className="bg-white p-4 sm:p-6 lg:p-8 rounded-xl sm:rounded-2xl shadow-lg border border-[#F8E8D2]">
            <h3 className="font-dm-sans font-bold text-gray-800 text-xl sm:text-2xl mb-4 sm:mb-6 text-center">Send us a Message</h3>
            <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
              <FormInput
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="Your Full Name"
                required
              />
              <FormInput
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="Your Email Address"
                required
              />
              <FormInput
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                placeholder="Your Phone Number"
              />
              <FormSelect
                name="subject"
                value={formData.subject}
                onChange={handleInputChange}
                required
              />
              <FormInput
                type="date"
                name="date"
                value={formData.date}
                onChange={handleInputChange}
              />
              <FormInput
                type="number"
                name="guests"
                value={formData.guests}
                onChange={handleInputChange}
                placeholder="Number of Guests (if applicable)"
                min="1"
              />
              <FormTextarea
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                placeholder="Tell us about your request or any special requirements..."
                rows="4"
                required
              />
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full inline-flex items-center justify-center gap-2 px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-primary to-primary-light text-black font-semibold rounded-lg sm:rounded-xl shadow-lg hover:-translate-y-1 hover:shadow-xl transition-all duration-200 text-sm sm:text-base disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i>
                    Sending...
                  </>
                ) : (
                  <>
                    <i className="fas fa-paper-plane"></i>
                    Send Message
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

const ContactInfoCard = memo(({ icon, title, content }) => (
  <div className="flex items-start gap-3 sm:gap-4 p-4 sm:p-6 bg-white rounded-xl sm:rounded-2xl shadow-lg border border-[#F8E8D2]">
    <i className={`${icon} text-primary text-xl sm:text-2xl mt-1`}></i>
    <div>
      <h3 className="font-dm-sans font-semibold text-gray-800 text-base sm:text-lg mb-1 sm:mb-2">{title}</h3>
      <p className="text-gray-600 text-sm sm:text-base" dangerouslySetInnerHTML={{ __html: content }}></p>
    </div>
  </div>
));

const FormInput = memo(({ type, name, value, onChange, placeholder, required, min }) => (
  <div>
    <input
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      required={required}
      min={min}
      className="w-full px-3 sm:px-4 py-2 sm:py-3 border-2 border-[#F8E8D2] rounded-lg sm:rounded-xl focus:border-primary focus:outline-none transition-all duration-200 text-sm sm:text-base"
    />
  </div>
));

const FormSelect = memo(({ name, value, onChange, required }) => (
  <div>
    <select
      name={name}
      value={value}
      onChange={onChange}
      required={required}
      className="w-full px-3 sm:px-4 py-2 sm:py-3 border-2 border-[#F8E8D2] rounded-lg sm:rounded-xl focus:border-primary focus:outline-none transition-all duration-200 text-sm sm:text-base"
    >
      <option value="">Select Your Inquiry</option>
      <option value="catering">Catering Services</option>
      <option value="private-event">Private Events</option>
      <option value="feedback">Feedback & Reviews</option>
      <option value="other">Other</option>
    </select>
  </div>
));

const FormTextarea = memo(({ name, value, onChange, placeholder, rows, required }) => (
  <div>
    <textarea
      name={name}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      rows={rows}
      required={required}
      className="w-full px-3 sm:px-4 py-2 sm:py-3 border-2 border-[#F8E8D2] rounded-lg sm:rounded-xl focus:border-primary focus:outline-none transition-all duration-200 resize-vertical text-sm sm:text-base"
    ></textarea>
  </div>
));

ContactInfoCard.displayName = 'ContactInfoCard';
FormInput.displayName = 'FormInput';
FormSelect.displayName = 'FormSelect';
FormTextarea.displayName = 'FormTextarea';

export default ContactSection; 